import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BaseLookupService } from "../../impl/BaseLookupService";
import { IClientLookupService } from "../IClientLookupService";
import { Injectable } from "@angular/core";
import { ClientAccountVisibilityService } from "../../../model/privatization/clientAccountVisibility/ClientAccountVisibilityService";
import { ClientAccountVisibilityType } from "../../../model/privatization/clientAccountVisibility/ClientAccountVisibilityType";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BranchLookupService } from "./BranchLookupService";
import { ClientModelPrivatization } from "shared/cubiq-services/model/privatization/ClientModelPrivatization";
import { AutoCompleteLabelValue } from "shared/cubiq-services/model/core/AutoCompleteLabelValue";
import { AutoCompleteLabelValueLookupService } from "./AutoCompleteLabelValueLookupService";

@Injectable()
export class ClientLookupService
  extends AutoCompleteLabelValueLookupService<string, CubiqTS.ClientImpl>
  implements IClientLookupService
{
  private myClientsMapLowercase: Map<any, any> = new Map();
  private clientContactMap: Map<any, Map<any, CubiqTS.ClientContact>> = new Map();
  private clientContactIdMap: Map<any, CubiqTS.ClientContact> = new Map();
  private tradingChannelProfileIdMap: Map<any, CubiqTS.TradingChannelProfile> = new Map();

  // user may not enter orders for these searchable clients
  protected visibleItemMap: Map<any, any> = new Map();
  //
  protected privatizationItemMap: Map<any, any> = new Map();

  // becnhamrkClients
  constructor(
    protected visibilityService: ClientAccountVisibilityService,
    protected branchService: BranchLookupService,
    protected clientModelPrivatization: ClientModelPrivatization,
    protected clientAccountVisibilityService: ClientAccountVisibilityService,
  ) {
    super(CubiqTS.ClientImpl);
  }

  itemAsIdLabelValue(item: CubiqTS.ClientImpl): AutoCompleteLabelValue {
    return {
      label: item.clientId,
      value: item.clientId,
      __bfx_visibility_type: ClientAccountVisibilityType.getVisibilityType(item),
    };
  }

  itemAsNameLabelValue(item: CubiqTS.ClientImpl): AutoCompleteLabelValue {
    return {
      label: item.clientName,
      value: item.clientId,
      __bfx_visibility_type: ClientAccountVisibilityType.getVisibilityType(item),
    };
  }

  static isMatch(
    token: string,
    client: { clientId: string; clientName: string; bookingCode: string },
  ) {
    let queryTokens =
      typeof token === "string"
        ? token
            .toLowerCase()
            .split(" ")
            .filter((token, i) => token != "" || i == 0)
        : [""];
    if (
      queryTokens.every(
        (token) =>
          (typeof client.clientId === "string" && client.clientId.toLowerCase().includes(token)) ||
          (typeof client.clientName === "string" &&
            client.clientName.toLowerCase().includes(token)) ||
          (typeof client.bookingCode === "string" &&
            client.bookingCode.toLowerCase().includes(token)),
      )
    ) {
      return true;
    }
    return false;
  }

  deleteItem(item: CubiqTS.ClientImpl): void {
    super.deleteItem(item);

    if (item != null && typeof item.clientId == "string") {
      this.myClientsMapLowercase.delete(item.clientId.toLowerCase());
    }
  }

  getItem(id: string): CubiqTS.ClientImpl {
    let client = super.getItem(id);

    if (!client && typeof id == "string") {
      let idLowercase = id.toLowerCase();
      client = this.myClientsMapLowercase.get(idLowercase);
    }

    if (!client) {
      client = this.visibleItemMap.get(id);
    }
    if (!client) {
      client = this.privatizationItemMap.get(id);
    }
    return client;
  }

  getTradingChannelProfiles(): CubiqTS.TradingChannelProfileImpl[] {
    return Array.from(this.tradingChannelProfileIdMap.values());
  }

  getTradingChannelProfileDisplayName(channelCd: string): string {
    if (this.tradingChannelProfileIdMap.has(channelCd)) {
      return this.tradingChannelProfileIdMap.get(channelCd).tradingSourceDisplayName;
    }
    return undefined;
  }

  getClient(id: string, type?: ClientAccountVisibilityType | null): CubiqTS.ClientImpl {
    // var ccyPair:CurrencyPairImpl
    let client = this.getItem(id);

    if (
      client != null &&
      type != null &&
      type !== ClientAccountVisibilityType.getVisibilityType(client)
    ) {
      // if we are of an incorrect ClientAccountVisibilityType
      // fufil not found contract of undefined rather than null
      return undefined;
    }

    return client;
  }

  mapItem(item: CubiqTS.ClientImpl): void | Promise<void> {
    const notifyUpdatedFn = this.visibilityService.updateTradableClients(item);

    return this.visibilityService.getVisibilityType(item).then((type: ClientAccountVisibilityType) => {
      ClientAccountVisibilityType.setVisibilityType(item, type);

      switch (type) {
        case ClientAccountVisibilityType.Full:
          super.mapItem(item);

          if (item != null && typeof item.clientId == "string")
            this.myClientsMapLowercase.set(item.clientId.toLowerCase(), item);

          this.mapItemInTreeSet(this.itemAsIdLabelValue(item), this.idLabelValueSet);
          this.mapItemInTreeSetByValue(this.itemAsNameLabelValue(item), this.nameLabelValueSet);

          break;
        case ClientAccountVisibilityType.Visible:
          super.mapItem(item); // possible error
          this.mapVisibleItem(item);

          this.mapItemInTreeSet(this.itemAsIdLabelValue(item), this.idLabelValueSet);
          this.mapItemInTreeSetByValue(this.itemAsNameLabelValue(item), this.nameLabelValueSet);

          break;
        case ClientAccountVisibilityType.Privatized:
          this.deleteItem(item);
          this.mapPrivatizationItem(item);
          break;
        case ClientAccountVisibilityType.None:
        default:
          // FIXME: Remove in production
          logger.out.warn(
            `${this.constructor.name}.mapItem() dropping client ${item.clientId} with ClientAccountVisibilityType.None`,
          );
          break;
      }

      notifyUpdatedFn();
    });
  }

  mapVisibleItem(item: CubiqTS.ClientImpl, id?: string): void {
    this.visibleItemMap.set(id ? id : item.clientId, item);
  }

  mapPrivatizationItem(item: CubiqTS.ClientImpl, id?: string): void {
    this.privatizationItemMap.set(id ? id : item.clientId, item);
  }

  mapItemById(item: CubiqTS.ClientImpl, key: string) {
    super.mapItemById(item, key);

    this.myClientsMapLowercase.set(key.toLowerCase(), item);
  }

  mapTradingChannelProfile(item: CubiqTS.TradingChannelProfile) {
    if (this.tradingChannelProfileIdMap.has(item.channelId) == false) {
      this.tradingChannelProfileIdMap.set(item.channelId, null);
    }

    this.removeTradingChannelProfile(item.channelId);
    this.tradingChannelProfileIdMap.set(item.channelId, item);
  }

  mapClientContact(item: CubiqTS.ClientContact): void {
    if (this.clientContactMap.has(item.clientCd) == false) {
      this.clientContactMap.set(item.clientCd, new Map());
    }

    this.removeClientContact(item.id.toString());

    this.clientContactMap.get(item.clientCd).set(item.id.toString(), item);
    this.clientContactIdMap.set(item.id.toString(), item);
  }

  getItemKey(item: CubiqTS.ClientImpl): string {
    // could be string or numeric id
    if (!item || typeof item.clientId != "string") return null;

    return String(item.clientId);
  }

  getClientContacts(clientCode: string, onlyActive: boolean = false): Array<CubiqTS.ClientContact> {
    let client = this.getActiveClient(clientCode);
    if (!onlyActive || client instanceof CubiqTS.ClientImpl) {
      if (!this.clientContactMap.has(clientCode)) {
        return [];
      }

      return Array.from(this.clientContactMap.get(clientCode).values());
    } else {
      return [];
    }
  }

  getClientContact(id: string): CubiqTS.ClientContact {
    return this.clientContactIdMap.get(id);
  }

  removeClientContact(id: string): CubiqTS.ClientContact {
    let clientContact: CubiqTS.ClientContact = this.clientContactIdMap.get(id);

    if (clientContact != null && typeof clientContact.clientCd == "string") {
      if (this.clientContactMap.has(clientContact.clientCd))
        this.clientContactMap.get(clientContact.clientCd).delete(clientContact.id.toString());
    }

    this.clientContactIdMap.delete(id);
    return clientContact;
  }

  removeTradingChannelProfile(id: string): CubiqTS.TradingChannelProfile {
    let tradingChannelProfile: CubiqTS.TradingChannelProfile =
      this.tradingChannelProfileIdMap.get(id);

    if (tradingChannelProfile != null && typeof tradingChannelProfile.channelId == "string") {
      if (this.tradingChannelProfileIdMap.has(tradingChannelProfile.channelId)) {
        this.tradingChannelProfileIdMap.delete(tradingChannelProfile.channelId);
      }
    }
    return tradingChannelProfile;
  }

  /* to be depricated */
  lookupClient(codeToken: string, maxResults: number): Array<CubiqTS.ClientImpl> {
    /*
            //old code
            let clients: Array<CubiqTS.ClientImpl> = [];
            let token: string = codeToken ? codeToken.toLowerCase() : codeToken;

            this.myItemMap.forEach((value, key) => {
                let client: CubiqTS.ClientImpl = value as CubiqTS.ClientImpl;
                if (client.clientId.toLowerCase().indexOf(token) >= 0 && (maxResults <= 0 || clients.length < maxResults)) {
                    clients.push(client);
                }
            });

            return clients; */
    return this.lookupClientsForId(codeToken, maxResults);
  }

  lookupClientsForMatch(
    codeToken: string,
    maxResults: number,
    onlyActive: boolean = false,
    autoFillEnabledOnly: boolean = false,
    benchmarkClients: boolean = false,
  ): Array<CubiqTS.ClientImpl> {
    let clients: Array<CubiqTS.ClientImpl> = [];

    for (const value of this.myItemMap.values()) {
      let client: CubiqTS.ClientImpl = value as CubiqTS.ClientImpl;
      if (typeof client.clientId != "string") continue;

      if (autoFillEnabledOnly && !client.autoFillEnabled) continue;

      if (benchmarkClients) {
        if (!this.isBenchmarkEnabledClient(client.clientId)) continue;
      }

      if (!onlyActive || client.ordersEnabledFg) {
        if (ClientLookupService.isMatch(codeToken, client)) {
          clients.push(client);
        }
      }
      if (maxResults > 0 && clients.length >= maxResults) {
        break;
      }
    }

    return clients;
  }

  lookupClientsForId(
    codeToken: string,
    maxResults: number,
    onlyActive: boolean = false,
    autoFillEnabledOnly: boolean = false,
    benchmarkClients: boolean = false,
  ): Array<CubiqTS.ClientImpl> {
    let clients: Array<CubiqTS.ClientImpl> = [];
    let token: string = codeToken ? codeToken.toLowerCase() : codeToken;

    for (const value of this.myItemMap.values()) {
      //((value, key) => {
      let client: CubiqTS.ClientImpl = value as CubiqTS.ClientImpl;
      if (typeof client.clientId != "string") continue;

      if (autoFillEnabledOnly && !client.autoFillEnabled) continue;

      if (benchmarkClients) {
        if (!this.isBenchmarkEnabledClient(client.clientId)) continue;
      }

      if (
        client.clientId.toLowerCase().indexOf(token) >= 0 &&
        (!onlyActive || client.ordersEnabledFg)
      ) {
        clients.push(client);
      }
      if (maxResults > 0 && clients.length >= maxResults) {
        break;
      }
    }

    return clients;
  }

  lookupClientsForName(
    codeToken: string,
    maxResults: number,
    onlyActive: boolean = false,
    autoFillEnabledOnly: boolean = false,
    benchmarkClients: boolean = false,
  ): Array<CubiqTS.ClientImpl> {
    let clients: Array<CubiqTS.ClientImpl> = [];
    let token: string = codeToken ? codeToken.toLowerCase() : codeToken;

    for (const value of this.myItemMap.values()) {
      //((value, key) => {
      let client: CubiqTS.ClientImpl = value as CubiqTS.ClientImpl;
      if (typeof client.clientId != "string") continue;

      if (autoFillEnabledOnly && !client.autoFillEnabled) continue;

      if (benchmarkClients) {
        // need to test the branch of the client
        let branchCd = client.branchCd;
        if (!this.isBenchmarkEnabledBranch(branchCd)) {
          continue;
        }
      }
      if (
        client.clientName.toLowerCase().indexOf(token) >= 0 &&
        (!onlyActive || client.ordersEnabledFg)
      ) {
        clients.push(client);
      }
      if (maxResults > 0 && clients.length >= maxResults) {
        break;
      }
    }

    return clients;
  }

  isBenchmarkEnabledClient(clientId: string): boolean {
    // need to test the branch of the client
    let client = this.myItemMap.get(clientId);
    if (client) {
      let branchCd = client.branchCd;
      return this.isBenchmarkEnabledBranch(branchCd);
    }
    return false;
  }

  isBenchmarkEnabledBranch(branchCd: string): boolean {
    let found: CubiqTS.BranchImpl = this.branchService.getItem(branchCd);
    return found && found.allowBenchmarkOrdersFg;
  }

  getActiveClient(id: string, type?: ClientAccountVisibilityType | null): CubiqTS.ClientImpl {
    let client = this.getItem(id);
    if (
      (client != null &&
        type != null &&
        type !== ClientAccountVisibilityType.getVisibilityType(client)) ||
      (client != null && !client.ordersEnabledFg)
    ) {
      // if we are of an incorrect ClientAccountVisibilityType fufil not found contract of undefined rather than null
      return undefined;
    } else {
      return client;
    }
  }

  /* ADDED TO BYPASS PRIMARY's PROMISES */
  lookupActiveClients(
    namePrefix: string,
    maxResults: number,
    onlyFullVisibility: boolean = false,
  ): Array<CubiqTS.ClientImpl> {
    return onlyFullVisibility
      ? this.filterFullyVisibleClients(this.lookupClientsForMatch(namePrefix, 0, true), maxResults)
      : this.lookupClientsForMatch(namePrefix, maxResults, true);
  }

  lookupActiveClientsForId(
    namePrefix: string,
    maxResults: number,
    onlyFullVisibility: boolean = false,
  ): Array<CubiqTS.ClientImpl> {
    return onlyFullVisibility
      ? this.filterFullyVisibleClients(this.lookupClientsForId(namePrefix, 0, true), maxResults)
      : this.lookupClientsForId(namePrefix, maxResults, true);
  }

  lookupActiveClientsForName(
    namePrefix: string,
    maxResults: number,
    onlyFullVisibility: boolean = false,
  ): Array<CubiqTS.ClientImpl> {
    return onlyFullVisibility
      ? this.filterFullyVisibleClients(this.lookupClientsForName(namePrefix, 0, true), maxResults)
      : this.lookupClientsForName(namePrefix, maxResults, true);
  }

  lookupActiveAFillClients(
    token: string,
    maxResults: number,
    onlyFullVisibility: boolean = false,
  ): Array<CubiqTS.ClientImpl> {
    return onlyFullVisibility
      ? this.filterFullyVisibleClients(this.lookupClientsForMatch(token, 0, true, true), maxResults)
      : this.lookupClientsForMatch(token, maxResults, true, true);
  }

  lookupAFillClientsForId(
    namePrefix: string,
    maxResults: number,
    onlyFullVisibility: boolean = false,
  ): Array<CubiqTS.ClientImpl> {
    return onlyFullVisibility
      ? this.filterFullyVisibleClients(
          this.lookupClientsForId(namePrefix, 0, true, true),
          maxResults,
        )
      : this.lookupClientsForId(namePrefix, maxResults, true, true);
  }

  lookupAFillClientsForName(
    namePrefix: string,
    maxResults: number,
    onlyFullVisibility: boolean = false,
  ): Array<CubiqTS.ClientImpl> {
    return onlyFullVisibility
      ? this.filterFullyVisibleClients(
          this.lookupClientsForName(namePrefix, 0, true, true),
          maxResults,
        )
      : this.lookupClientsForName(namePrefix, maxResults, true, true);
  }

  lookupActiveBMarkClients(
    token: string,
    maxResults: number,
    onlyFullVisibility: boolean = false,
  ): Array<CubiqTS.ClientImpl> {
    return onlyFullVisibility
      ? this.filterFullyVisibleClients(
          this.lookupClientsForMatch(token, 0, true, false, true),
          maxResults,
        )
      : this.lookupClientsForMatch(token, maxResults, true, false, true);
  }

  lookupBMarkClientsForId(
    id: string,
    maxResults: number,
    onlyFullVisibility: boolean = false,
  ): Array<CubiqTS.ClientImpl> {
    return onlyFullVisibility
      ? this.filterFullyVisibleClients(
          this.lookupClientsForId(id, 0, true, false, true),
          maxResults,
        )
      : this.lookupClientsForId(id, maxResults, true, false, true);
  }

  lookupBMarkClientsForName(
    namePrefix: string,
    maxResults: number,
    onlyFullVisibility: boolean = false,
  ): Array<CubiqTS.ClientImpl> {
    return onlyFullVisibility
      ? this.filterFullyVisibleClients(
          this.lookupClientsForName(namePrefix, 0, true, false, true),
          maxResults,
        )
      : this.lookupClientsForName(namePrefix, maxResults, true, false, true);
  }

  lookupClientsForSalesGroup(
    salesGroupId: CubiqTS.Long,
    maxResults: number,
    onlyActive: boolean = false,
    autoFillEnabledOnly: boolean = false,
    benchmarkClients: boolean = false,
    onlyFullVisibility: boolean = false,
  ): Array<CubiqTS.ClientImpl> {
    let clients: Array<CubiqTS.ClientImpl> = [];
    if (salesGroupId instanceof CubiqTS.Long) {
      for (const value of this.myItemMap.values()) {
        //((value, key) => {
        let client: CubiqTS.ClientImpl = value as CubiqTS.ClientImpl;
        if (typeof client.clientId != "string") continue;

        if (onlyActive && !client.ordersEnabledFg) continue;

        if (autoFillEnabledOnly && !client.autoFillEnabled) continue;

        if (benchmarkClients) {
          // need to test the branch of the client
          let branchCd = client.branchCd;
          if (!this.isBenchmarkEnabledBranch(branchCd)) {
            continue;
          }
        }
        if (
          client.salesGroupId instanceof CubiqTS.Long &&
          client.salesGroupId.equals(salesGroupId)
        ) {
          clients.push(client);
        }

        if (maxResults > 0 && clients.length >= maxResults) {
          break;
        }
      }
    }
    return onlyFullVisibility ? this.filterFullyVisibleClients(clients, maxResults) : clients;
  }

  /// OMS-31021
  // Filters the dataset of clients, based on users visibility, only fully visible clients are returned
  private filterFullyVisibleClients(
    dataset: Array<CubiqTS.ClientImpl>,
    maxResults: number,
  ): Array<CubiqTS.ClientImpl> {
    if (Array.isArray(dataset) && dataset.length > 0) {
      let result: Array<CubiqTS.ClientImpl> = new Array<CubiqTS.ClientImpl>();
      for (let client of dataset) {
        if (
          client instanceof CubiqTS.ClientImpl &&
          this.clientAccountVisibilityService._getVisibilityType(client) ===
            ClientAccountVisibilityType.Full
        ) {
          result.push(client);
        }

        if (maxResults > 0 && result.length >= maxResults) {
          break;
        }
      }
      return result;
    } else {
      return [];
    }
  }
}
